import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { List, MailchimpAudience, MailchimpImport as MailchimpImportRecord, MailchimpTag } from '../../types'
import Alert from '../../ui/Alert'
import Button from '../../ui/Button'
import ProgressBar from '../../ui/ProgressBar'
import { MultiSelect } from '../../ui/form/MultiSelect'
import { SingleSelect } from '../../ui/form/SingleSelect'
import TextInput from '../../ui/form/TextInput'
import FormWrapper from '../../ui/form/FormWrapper'
import Modal, { ModalStateProps } from '../../ui/Modal'
import './MailchimpImportModal.css'

type Step = 'connect' | 'configure' | 'processing'

const NO_LIST = 0

export default function MailchimpImportModal({ open, onClose }: ModalStateProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)

    const [step, setStep] = useState<Step>('connect')
    const [apiKey, setApiKey] = useState('')
    const [audiences, setAudiences] = useState<MailchimpAudience[]>([])
    const [lists, setLists] = useState<List[]>([])
    const [audienceId, setAudienceId] = useState<string>('')
    const [listId, setListId] = useState<number>(NO_LIST)
    const [tags, setTags] = useState<MailchimpTag[]>([])
    const [tagsLoading, setTagsLoading] = useState(false)
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [record, setRecord] = useState<MailchimpImportRecord | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const pollRef = useRef<ReturnType<typeof setInterval>>()

    const emailIdentificationOff = !project.allow_email_identification

    useEffect(() => () => {
        if (pollRef.current) clearInterval(pollRef.current)
    }, [])

    // Tags belong to a specific audience, so refetch (and clear the prior
    // selection) whenever the chosen audience changes.
    useEffect(() => {
        if (step !== 'configure' || !audienceId || !apiKey) return
        let cancelled = false
        setTagsLoading(true)
        setTags([])
        setSelectedTagIds([])
        api.mailchimp.tags(project.id, apiKey, audienceId)
            .then(fetched => { if (!cancelled) setTags(fetched) })
            .catch(() => { if (!cancelled) setTags([]) })
            .finally(() => { if (!cancelled) setTagsLoading(false) })
        return () => { cancelled = true }
    }, [step, audienceId, apiKey, project.id])

    const handleConnect = async ({ api_key }: { api_key: string }) => {
        setError(undefined)
        try {
            const [fetchedAudiences, listPage] = await Promise.all([
                api.mailchimp.audiences(project.id, api_key),
                api.lists.search(project.id, { limit: 9999, filter: { all_folders: true } }),
            ])
            setApiKey(api_key)
            setAudiences(fetchedAudiences)
            setLists(listPage.results)
            setAudienceId(fetchedAudiences[0]?.id ?? '')
            setStep('configure')
        } catch (err: any) {
            setError(err.response?.data?.error ?? err.message)
        }
    }

    const startPolling = (importId: string) => {
        if (pollRef.current) clearInterval(pollRef.current)
        pollRef.current = setInterval(async () => {
            try {
                const updated = await api.mailchimp.imports.get(project.id, importId)
                setRecord(updated)
                if (updated.state === 'complete' || updated.state === 'failed') {
                    if (pollRef.current) clearInterval(pollRef.current)
                }
            } catch {
                // Ignore transient polling errors
            }
        }, 2000)
    }

    const handleStartImport = async () => {
        if (!audienceId) return
        setIsSubmitting(true)
        setError(undefined)
        try {
            const created = await api.mailchimp.imports.create(project.id, {
                api_key: apiKey,
                audience_id: audienceId,
                list_id: listId === NO_LIST ? undefined : listId,
                tags: tags
                    .filter(tag => selectedTagIds.includes(tag.id))
                    .map(({ id, name }) => ({ id, name })),
            })
            setRecord(created)
            setStep('processing')
            startPolling(created.id)
        } catch (err: any) {
            setError(err.response?.data?.error ?? err.message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const reset = () => {
        if (pollRef.current) clearInterval(pollRef.current)
        setStep('connect')
        setApiKey('')
        setAudiences([])
        setAudienceId('')
        setListId(NO_LIST)
        setTags([])
        setTagsLoading(false)
        setSelectedTagIds([])
        setRecord(null)
        setError(undefined)
        setIsSubmitting(false)
    }

    const handleClose = () => {
        reset()
        onClose(false)
    }

    const selectedAudience = audiences.find(a => a.id === audienceId)

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={t('import_mailchimp')}
            description={t('import_mailchimp_description')}
            size="regular"
        >
            {error && <Alert variant="error" title={t('error')}>{error}</Alert>}

            {emailIdentificationOff && (
                <Alert variant="warn" title={t('mailchimp_email_identification_required')}>
                    {t('mailchimp_email_identification_required_body')}{' '}
                    <Link to="../">{t('mailchimp_go_to_general_settings')}</Link>
                </Alert>
            )}

            {step === 'connect' && (
                <FormWrapper<{ api_key: string }>
                    onSubmit={handleConnect}
                    submitLabel={t('mailchimp_connect')}
                >
                    {form => <>
                        <p>{t('mailchimp_connect_help')}</p>
                        <TextInput.Field
                            form={form}
                            name="api_key"
                            label={t('mailchimp_api_key')}
                            subtitle={t('mailchimp_api_key_help')}
                            type="password"
                            required
                        />
                    </>}
                </FormWrapper>
            )}

            {step === 'configure' && (
                <div className="mailchimp-configure">
                    <SingleSelect
                        label={t('mailchimp_audience')}
                        value={audienceId}
                        onChange={setAudienceId}
                        options={audiences}
                        toValue={a => a.id}
                        getValueKey={v => v}
                        getOptionDisplay={a => `${a.name} (${a.member_count.toLocaleString()} ${t('mailchimp_contacts')})`}
                    />

                    <SingleSelect
                        label={t('mailchimp_list_optional')}
                        subtitle={t('mailchimp_list_help')}
                        value={listId}
                        onChange={setListId}
                        options={[{ id: NO_LIST, name: t('mailchimp_no_list') }, ...lists]}
                        toValue={l => l.id}
                        getValueKey={v => v}
                        getOptionDisplay={l => l.name}
                    />

                    {(tagsLoading || !tags.length)
                        ? (
                            <div className="mailchimp-tags">
                                <span className="mailchimp-tags-label">{t('mailchimp_tags_label')}</span>
                                <p className="mailchimp-tags-empty">
                                    {tagsLoading ? t('mailchimp_tags_loading') : t('mailchimp_tags_none')}
                                </p>
                            </div>
                        )
                        : (
                            <MultiSelect
                                label={t('mailchimp_tags_label')}
                                subtitle={t('mailchimp_tags_help')}
                                value={selectedTagIds}
                                onChange={setSelectedTagIds}
                                options={tags}
                                toValue={tag => tag.id}
                                getValueKey={v => v}
                                getOptionDisplay={tag => `${tag.name} · ${t('mailchimp_tag_members', { count: tag.member_count })}`}
                                getSelectedOptionDisplay={tag => tag.name}
                                placeholder={t('mailchimp_tags_placeholder')}
                                searchable
                                optionsFooter={
                                    <div
                                        className="mailchimp-tags-bulk"
                                        onMouseDown={e => e.preventDefault()}
                                    >
                                        <Button
                                            variant="plain"
                                            size="tiny"
                                            onClick={() => setSelectedTagIds(tags.map(tag => tag.id))}
                                            disabled={selectedTagIds.length === tags.length}
                                        >
                                            {t('mailchimp_tags_select_all')}
                                        </Button>
                                        <Button
                                            variant="plain"
                                            size="tiny"
                                            onClick={() => setSelectedTagIds([])}
                                            disabled={selectedTagIds.length === 0}
                                        >
                                            {t('mailchimp_tags_select_none')}
                                        </Button>
                                    </div>
                                }
                            />
                        )}

                    {selectedAudience && (
                        <Alert variant="plain" title={t('mailchimp_summary_title')}>
                            {t('mailchimp_summary_import', { count: selectedAudience.member_count })}
                            {selectedAudience.unsubscribe_count > 0 && (
                                <> {t('mailchimp_summary_unsubscribed', { count: selectedAudience.unsubscribe_count })}</>
                            )}
                            {' '}{t('mailchimp_summary_pending_skipped')}
                        </Alert>
                    )}

                    <div className="mailchimp-actions">
                        <Button variant="secondary" onClick={reset}>{t('back')}</Button>
                        <Button
                            onClick={handleStartImport}
                            isLoading={isSubmitting}
                            disabled={!audienceId || emailIdentificationOff || isSubmitting}
                        >
                            {selectedAudience
                                ? t('mailchimp_start_import', { count: selectedAudience.member_count })
                                : t('mailchimp_start_import_generic')}
                        </Button>
                    </div>
                </div>
            )}

            {step === 'processing' && record && (
                <ProcessingStep record={record} onDone={handleClose} />
            )}
        </Modal>
    )
}

function ProcessingStep({ record, onDone }: { record: MailchimpImportRecord, onDone: () => void }) {
    const { t } = useTranslation()

    if (record.state === 'complete') {
        return (
            <div className="mailchimp-processing">
                <Alert variant="success" title={t('mailchimp_import_complete')}>
                    {t('mailchimp_import_complete_body', { count: record.processed_count })}
                    {record.skipped_count > 0 && (
                        <> {t('mailchimp_import_skipped_body', { count: record.skipped_count })}</>
                    )}
                </Alert>
                {record.created_lists && record.created_lists.length > 0 && (
                    <Alert variant="plain" title={t('mailchimp_lists_created_title')}>
                        <p>{t('mailchimp_lists_created_body')}</p>
                        <ul className="mailchimp-created-lists">
                            {record.created_lists.map(list => (
                                <li key={list.id}>{list.name}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                <div className="mailchimp-actions">
                    <Button onClick={onDone}>{t('done')}</Button>
                </div>
            </div>
        )
    }

    if (record.state === 'failed') {
        return (
            <div className="mailchimp-processing">
                <Alert variant="error" title={t('mailchimp_import_failed')}>
                    {record.error ?? t('unknown_error')}
                </Alert>
                <div className="mailchimp-actions">
                    <Button variant="secondary" onClick={onDone}>{t('back')}</Button>
                </div>
            </div>
        )
    }

    const known = record.row_count != null && record.row_count > 0
    const percent = known ? Math.round((record.processed_count / record.row_count!) * 100) : 0

    return (
        <div className="mailchimp-processing">
            <p>{t('mailchimp_processing')}</p>
            <ProgressBar
                percent={percent}
                label={known
                    ? `${record.processed_count.toLocaleString()} / ${record.row_count!.toLocaleString()} (${percent}%)`
                    : t('mailchimp_processing_counting')}
            />
        </div>
    )
}
